import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getPermissions } from "store/userSlice";
import { api } from "helpers/axios";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { errorMessage, fileDownload, fileDownloadResponse, formatFixedDigits, formatMoney, formatNumberTo } from "helpers/common";
import { fetchBudgetSettings, getBudgetSettings, getBudgetSettingsError } from "store/budgetSettingsSlice";
import { tableResize } from "helpers/tableResize";
import { useForm } from "react-hook-form";
import TextTruncate from "components/ui/TextTruncate";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import usePinnedHead from "hooks/usePinnedHead";
import ExportForm from "components/Forms/Export";
import ErrorAlert from "components/ui/ErrorAlert";
import hasPermissionTo from "helpers/permission";
import CalculationResultTreeView from "./CalculationResultTreeView";
import { useTranslation } from "translation/useTranslation";
import { fetchFields, getAllCustomNames, getAllFields, getBalancedScorecardFields, getCalculationRelatedFields, getConditionalFields, getFixedFields, getFreeFields, getSystemFields } from "store/fieldsSlice";
import EmployeeTableColumn from "components/EmployeeManagement/EmployeeTableColumn";
import ListPagination from "components/ui/ListPagination";
import { Button, Select } from "components/ui";

export default function Results() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();


  const { translate, editableTranslate } = useTranslation();

  const budgetError = useSelector(getBudgetSettingsError);
  const budgets = useSelector(getBudgetSettings);

  const permissions = useSelector(getPermissions);

  const allFields = useSelector(getAllFields);

  const fieldsFixed = useSelector(getFixedFields);
  const fieldsCalculationRelated = useSelector(getCalculationRelatedFields);
  const fieldsConditional = useSelector(getConditionalFields);
  const fieldsSystem = useSelector(getSystemFields);
  const fieldsFree = useSelector(getFreeFields);
  const fieldsBs = useSelector(getBalancedScorecardFields);

  const fieldCustomNames = useSelector(getAllCustomNames);

  const [displayedEmployeeFields, displayedLkFields] = useMemo(() => {
    if (allFields) {
      return [
        allFields.filter(f => !f.is_balanced_scorecard_specific && f.is_active && f.display_in.includes('calculation_result')),
        allFields.filter(f => f.is_balanced_scorecard_specific && f.is_active && f.display_in.includes('calculation_result')),
      ];
    }
    return [[], []];
  }, [allFields]);

  const [error, setError] = useState(null);

  const [selectedBudget, setSelectedBudget] = useState(null);

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailsPdfLoading, setDetailsPdfLoading] = useState(false);
  const [detailsPdfError, setDetailsPdfError] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    perPage: 20
  });

  const [counter, setCounter] = useState(0);

  const [sortCols, setSortCols] = useState([['last_name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const [template, setTemplate] = useState(null);

  const [showExport, setShowExport] = useState(false);

  const exportFields = useMemo(() => {
    if (!template || !fieldsFixed) return [];

    return [
      ...fieldsFixed,
      ...fieldsCalculationRelated,
      ...fieldsConditional,
      ...fieldsSystem,
      ...fieldsFree,
      ...fieldsBs,
      ...template.columns]
      .filter(f => f.hasOwnProperty('is_active') ? f.is_active : true)
      .map(f => ({
        ...f,
        key: f?.key || f.translation_key.replace('field.', ''),
        displayed: f.hasOwnProperty('display_in') ? f.display_in.includes('calculation_result') : ['capping', 'total_payout', 'calculation_result'].includes(f?.key)
      }));
  }, [fieldsFixed, fieldsCalculationRelated, fieldsConditional, fieldsSystem, fieldsFree, fieldsBs, template]);

  const objSortCols = useMemo(() => {
    const obj = {};

    for (const sc of sortCols) {
      obj[sc[0]] = sc[1];
    }

    return obj;
  }, [sortCols]);

  const budgetOptions = useMemo(() => {
    if (!budgets) return [];

    const filtered = budgets.filter(b => b.calculation_processes?.final_calculation?.type === 'final_calculation' && b.calculation_processes?.final_calculation?.status === 'completed');

    const mapped = filtered.map(b => ({ value: b.hash_id, label: b.name }));
    mapped.unshift({ value: '-', label: translate('calculation_overview.all_budgets') }, { value: 'disabled', label: '----------', isDisabled: true });

    return mapped;
  }, [budgets]);

  const pinnedHeadRef = useRef(null);

  const tableRef = useRef(null);
  const listFetched = useRef(false);

  const theadRef = useRef(null);
  usePinnedHead(pinnedHeadRef, theadRef);

  useEffect(() => {
    if (budgetError) setError(budgetError);
  }, [budgetError]);

  useEffect(() => {
    if (budgetOptions.length < 1) return;

    const queryBudgetHashId = searchParams.get('budget_hash_id');
    const matchedBudget = queryBudgetHashId ? budgetOptions.find(option => option.value === queryBudgetHashId) : null;

    setSelectedBudget(matchedBudget || budgetOptions[0]);
  }, [budgetOptions, searchParams]);

  useEffect(() => {
    if (selectedBudget) {
      if (pagination.page > 1) setPagination({ ...pagination, page: 1 });
      else setCounter(prev => prev + 1);
    } else {
      setEmployees([]);
      setPagination({ ...pagination, totalPages: 1, page: 1 });
    }
  }, [selectedBudget]);

  useEffect(() => {
    setCounter(prev => prev + 1);
  }, [pagination.page, pagination.perPage]);

  useEffect(() => {
    if (counter > 0) fetchEmployeesResult();
  }, [counter, sortCols]);

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 250,
    email: 300,
    calculation_pay_factor: 250,
    individual_pay_factor: 250,
    individual_average_rating: 300,
    exclude_from_payout: 300,
    balanced_scorecard_template: 400,
    lk: 100
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'calculation_result', false, {}, maxColWidth, setResizedCount);
  }, [employees, triggerColWidth, selectedEmployee, showExport]);

  useEffect(() => {
    dispatch(fetchFields());

    fetchAllBudgets();

    fetchTemplate();
  }, []);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [showExport]);

  const fetchAllBudgets = () => {
    dispatch(fetchBudgetSettings('calculation_process'));
  };

  const fetchEmployeesResult = async (page = null) => {
    if (!selectedBudget) return;

    setLoading(true);
    // the first list request keeps errors from the mount requests (template, budgets)
    if (listFetched.current) setError(null);
    listFetched.current = true;

    if (!page) page = pagination.page;

    try {
      let url = `/employees/calculation-result?limit=${pagination.perPage}&page=${page}&budget_hash_id=${selectedBudget.value}`;

      if (sortCols && sortCols.length > 0) {
        url += `&sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }

      const { status, data } = await api.get(url);

      if (status === 200) {
        setEmployees(data.data);
        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: +temp.total_pages, perPage: +temp.per_page });
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch employee result'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
    setDetailsPdfError(null);
    // setResultTreeData({
    //   employees: [
    //     {
    //       id: employee.uuid,
    //       scoreCards: employee.balanced_scorecards.map(bs => ({
    //         id: bs.uuid,
    //         calculationSteps: bs.calculation?.logging || [],
    //         calculationResult: bs.calculation?.result || {}
    //       }))
    //     }
    //   ]
    // });
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(sortCols.filter(sc => sc[0] === field));
    }
  };

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const fetchTemplate = async () => {
    try {
      const { data, status } = await api.get(`/exports/templates/standard_payout_report`);
      if (status === 200) {
        setTemplate(data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch template'));
      Sentry.captureException(err);
    }
  };

  const handleExport = (e) => {
    e?.currentTarget?.blur();
    setShowExport(!showExport);
  };

  const handleCalculationDetailsPdf = async () => {
    setDetailsPdfError(null);
    setDetailsPdfLoading(true);

    try {
      const response = await api.post('/exports/calculation-details', {
        employee_hash_id: selectedEmployee.hash_id,
        budget_hash_id: selectedBudget?.value || '-'
      }, { responseType: 'blob' });

      fileDownloadResponse(response, 'berechnungsdetails.pdf');
    } catch (err) {
      let errObj = err;
      if (err.response?.data instanceof Blob) {
        try {
          errObj = JSON.parse(await err.response.data.text());
        } catch (e) {
          // not JSON
        }
      }
      setDetailsPdfError(errorMessage(errObj, 'generate calculation details PDF report'));
      Sentry.captureException(err);
    } finally {
      setDetailsPdfLoading(false);
    }
  };

  const buildCalculationResultTreeData = (calculation) => {
    return {
      calculationSteps: calculation?.logging || []
    };
  };

  const getEmployeeScorecards = (employee) => employee?.balanced_scorecards?.data || [];

  const parseCalculationNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'number') return Number.isNaN(value) ? null : value;

    const parsed = Number(formatNumberTo(value, '.'));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const formatCalculationAmount = (value, suffix = '') => {
    const parsed = parseCalculationNumber(value);
    return parsed === null ? '' : `${formatMoney(parsed)}${suffix}`;
  };

  const getScorecardFinalTotal = (scorecard) => parseCalculationNumber(scorecard?.calculation?.result?.total);

  const getEmployeeFinalTotal = (employee) => {
    const scorecardTotals = getEmployeeScorecards(employee)
      .map(getScorecardFinalTotal)
      .filter(total => total !== null);

    if (scorecardTotals.length === 0) return null;

    return scorecardTotals.reduce((sum, total) => sum + total, 0);
  };

  const hasCalculationResults = (employee) => getEmployeeScorecards(employee)
    .some(scorecard => getScorecardFinalTotal(scorecard) !== null);

  return <>
    <div className={`col-lg ${showExport ? 'col-md-9 section-col-right' : ''} min-height-after-navbar`}>
      <div className="bg-white w-100 p-4 content-card h-100">
        <div ref={pinnedHeadRef} className="pinned-head">
          <div className="d-flex justify-content-between align-items-center border-bottom employee-management-tabs-div mb-4">
            <div className="employee-management-tabs">
              <div data-testid="tab-preliminary-check" className={`cursor-pointer`} onClick={() => navigate('/calculation/preliminary-check')}>
                <span>{editableTranslate('calculation_overview.not_calculated')}</span>
              </div>
              <div data-testid="tab-results" className={`cursor-pointer active`} onClick={() => navigate('/calculation/results')}>
                <span>{editableTranslate('calculation_overview.results')}</span>
              </div>
            </div>
            <div className="position-relative d-flex align-items-center z-100">
              <div style={{ display: 'contents' }} data-testid="result-select">
                <Select placeholder={translate('form.select_placeholder')} options={budgetOptions} value={selectedBudget} onChange={v => setSelectedBudget(v)} isClearable={true} />
              </div>
              {
                hasPermissionTo('reports.calculation.*', permissions) && <Button variant="secondary" onClick={handleExport} data-testid="export-button" className="ml-2"><i className="bi bi-file-earmark-arrow-down mr-1"></i> {editableTranslate('global.export', 'Bericht/Export')}</Button>
              }
              <div className="employee-filters-separator"></div>
            </div>
          </div>

          {
            error &&
            <div className="mt-3">
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }
        </div>

        <div>
          <div className={`${selectedEmployee ? 'calculation-page-2-col' : ''} d-flex`}>
            <div className={`${selectedEmployee ? 'calculation-left-div' : 'w-100'}`}>
              <div className="table-responsive has-custom-scrollbar">
                <table ref={tableRef} className="table calculation-result-table" data-testid="employees-table">
                  <thead ref={theadRef} className="sticky-top">
                    <tr>
                      {
                        displayedEmployeeFields.map((field, i) => {
                          return <th key={i} scope="col" className={`cursor-pointer ${field.alignment ? 'text-' + field.alignment : ''}`}>
                            <span className="float-right floating-icons">
                              <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols?.[field.slug] ? (objSortCols?.[field.slug] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field.slug)} data-testid={`sort-${field.slug}`} />
                              {
                                objSortCols?.[field.slug] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field.slug)} data-testid={`sort-pin-${field.slug}`} />
                              }
                            </span>
                            <span data-testid={`sort-${field.slug}-label`} className="th-span" onClick={() => handleChangeSortCol(field.slug)}>
                              {field?.custom_name || editableTranslate(field.translation_key, field.name)}
                              {field.name === 'Trend' && <>
                                <i className="bi bi-info-circle ml-1" data-tooltip-id={`th-trend-tooltip`}></i>
                                <ReactTooltip id={`th-trend-tooltip`} place="bottom"
                                  content={editableTranslate('tooltips.field.trend.tooltip')} />
                              </>}
                              <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                              <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                            </span>
                            <div className="th-resize-div" data-field={field.slug}></div>
                          </th>
                        })
                      }
                      <th className="text-right">
                        <span className="th-span">
                          {editableTranslate('calculation_overview.total_payout')}
                          <i className="bi bi-info-circle ml-1" data-tooltip-id="calculation-rounded-tooltip" data-tooltip-place="bottom"></i>
                          <ReactTooltip id="calculation-rounded-tooltip" content={translate('tooltips.calculation_overview.rounded')} />
                        </span>
                        <div className="th-resize-div" data-field={'total_payout'}></div>
                      </th>
                      <th className="text-right">
                        <span className="th-span">
                          {editableTranslate('calculation_overview.calculation_result')}
                        </span>
                        <div className="th-resize-div" data-field={'calculation_result'}></div>
                      </th>
                      <th>
                        <span className="th-span">
                          {editableTranslate('calculation_overview.capping')}
                        </span>
                        <div className="th-resize-div" data-field={'capping'}></div>
                      </th>
                      {
                        displayedLkFields.map((field, i) => {
                          return <th key={i} scope="col" className={`cursor-pointer ${field.alignment ? 'text-' + field.alignment : ''}`}>
                            <span className="float-right floating-icons">
                              <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols?.[field.slug] ? (objSortCols?.[field.slug] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field.slug)} data-testid={`sort-${field.slug}`} />
                              {
                                objSortCols?.[field.slug] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field.slug)} data-testid={`sort-pin-${field.slug}`} />
                              }
                            </span>
                            <span data-testid={`sort-${field.slug}-label`} className="th-span" onClick={() => handleChangeSortCol(field.slug)}>
                              {field?.custom_name || editableTranslate(field.translation_key, field.name)}
                              {field.name === 'Trend' && <>
                                <i className="bi bi-info-circle ml-1" data-tooltip-id={`th-trend-tooltip`}></i>
                                <ReactTooltip id={`th-trend-tooltip`} place="bottom"
                                  content={editableTranslate('tooltips.field.trend.tooltip')} clickable />
                              </>}
                              <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                              <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                            </span>
                            <div className="th-resize-div" data-field={field.slug}></div>
                          </th>
                        })
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {
                      !loading && employees.map((employee, i) => <tr data-testid={`row-${employee?.hash_id}`} key={i} className={`${selectedEmployee?.hash_id === employee.hash_id ? 'selected-row' : ''} table-row`} onClick={() => handleSelectEmployee(employee)}>
                        {
                          displayedEmployeeFields.map((field, i) => {
                            return <EmployeeTableColumn key={i} field={field} employee={employee} sortCols={sortCols} resizedCount={resizedCount} />
                          })
                        }
                        <td className="text-right">
                          <TextTruncate text={formatCalculationAmount(getEmployeeFinalTotal(employee), ' €')} resizedCount={resizedCount} />
                        </td>
                        <td className="text-right">
                          {
                            getEmployeeScorecards(employee).length > 0 && getEmployeeScorecards(employee).map((lk, j) => {
                              const finalTotal = getScorecardFinalTotal(lk);
                              const cappingBefore = lk.calculation?.result?.capping?.capped
                                ? formatCalculationAmount(lk.calculation?.result?.capping?.amount?.before, ' €')
                                : '';

                              return <div key={j} className="justify-content-end employee-lk-text">
                                <div className="d-flex flex-column align-items-end text-right w-100">
                                  <span>{finalTotal !== null ? formatCalculationAmount(finalTotal, ' €') : ' '}</span>
                                  {
                                    cappingBefore &&
                                    <small className="text-muted">
                                      {editableTranslate('calculation_overview.before_capping_amount', 'vor Deckelung:')} {cappingBefore}
                                    </small>
                                  }
                                </div>
                              </div>
                            })
                          }
                        </td>
                        <td>
                          {
                            getEmployeeScorecards(employee).length > 0 && getEmployeeScorecards(employee).map((lk, j) => {
                              const capped = !!lk.calculation?.result?.capping?.capped;
                              const capValue = capped ? formatCalculationAmount(lk.calculation?.result?.capping?.amount?.after, ' €') : '';

                              return <div key={j} className="employee-lk-text">
                                <div className="d-flex flex-column text-right w-100">
                                  <span>{capped ? translate('global.yes', 'Ja') : translate('global.no', 'Nein')}</span>
                                  {
                                    capValue &&
                                    <small className="text-muted">
                                      {editableTranslate('calculation_overview.capped_to_amount', 'auf')} {capValue}
                                    </small>
                                  }
                                </div>
                              </div>;
                            })
                          }
                        </td>
                        {
                          displayedLkFields.map((field, i) => {
                            return <EmployeeTableColumn key={i} field={field} employee={employee} sortCols={sortCols} resizedCount={resizedCount} useCalculationPayFactor={true} />
                          })
                        }
                      </tr>)
                    }
                    {
                      loading && <tr>
                        <td colSpan={displayedEmployeeFields.length + 3 + displayedLkFields.length}>
                          <div className="d-flex justify-content-center" data-testid="loading">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Wird geladen...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                    {
                      (!loading && employees.length === 0) && <tr>
                        <td colSpan={displayedEmployeeFields.length + 3 + displayedLkFields.length} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
              <TableScrollbar tableRef={tableRef} data={employees} trigger={resizedCount} />
              <ListPagination
                page={pagination.page}
                perPage={pagination.perPage}
                totalPages={pagination.totalPages}
                totalRows={pagination.totalRows}
                setPage={(page) => setPagination({ ...pagination, page })}
                setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
              />
            </div>
            {
              selectedEmployee && <div className="mt-3 calculation-right-div" data-testid="calculation-details">
                <div className="calculation-right-header">
                  <h5 className="font-weight-bold">{editableTranslate('calculation_overview.calculation_details', 'Detailberechnung')}</h5>
                  <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => handleSelectEmployee(null)} data-testid="close-button"><i className="bi bi-x-lg"></i></span>
                </div>
                <div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                    </div>
                    <div>
                      {
                        hasCalculationResults(selectedEmployee) &&
                        <Button variant="primary" onClick={handleCalculationDetailsPdf} loading={detailsPdfLoading} data-testid="download-report-button">
                          {!detailsPdfLoading && <i className="bi bi-download"></i>} {editableTranslate('balanced_scorecard.report_download')}
                        </Button>
                      }
                    </div>
                  </div>
                  <ErrorAlert error={detailsPdfError} onClose={() => setDetailsPdfError(null)} />
                  <div className="general-info mt-3">
                    <div className="row">
                      <div className="col">
                        <span>{fieldCustomNames?.['field.first_name'] || editableTranslate('field.first_name')}</span>
                        <span className="info-field-value">{selectedEmployee.first_name}</span>
                      </div>
                      <div className="col">
                        <span>{fieldCustomNames?.['field.last_name'] || editableTranslate('field.last_name')}</span>
                        <span className="info-field-value">{selectedEmployee.last_name}</span>
                      </div>
                      <div className="col">
                        <span>{fieldCustomNames?.['field.employee_number'] || editableTranslate('field.employee_number')}</span>
                        <span className="info-field-value">{selectedEmployee.employee_number}</span>
                      </div>
                    </div>
                  </div>
                  <hr />
                  {
                    !hasCalculationResults(selectedEmployee) &&
                    <div className="alert alert-warning" role="alert">
                      {editableTranslate('calculation_overview.calculation_details.no_result', 'Kein Ergebnis verfügbar.')}
                    </div>
                  }
                  {
                    hasCalculationResults(selectedEmployee) && <>
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-title">{editableTranslate('calculation_overview.calculation_details.overall_result', 'Gesamtergebnis')}</h5>
                          <div className="row">
                            <div className="col col-md-12">
                              <dl className="row">
                                <dt className="col-sm-4">{editableTranslate('calculation_overview.total_payout_total', 'Auszahlung gesamt')}</dt>
                                <dd className="col-sm-7">{formatCalculationAmount(getEmployeeFinalTotal(selectedEmployee), ' €')}</dd>
                                <dt className="col-sm-4">{editableTranslate('global.note', 'Hinweis')}</dt>
                                <dd className="col-sm-7 text-muted">{editableTranslate('calculation_overview.total_payout_visible_scorecards_hint', 'Aus den sichtbaren Leistungskarten summiert.')}</dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  }
                  {
                    getEmployeeScorecards(selectedEmployee).length > 0 && hasCalculationResults(selectedEmployee) &&
                    getEmployeeScorecards(selectedEmployee).map((lk, i) => {
                      return (
                        <div className="card mt-3" key={'bsc_' + i}>
                          <div className="card-body">
                            <h5 className="card-title">{editableTranslate('calculation_overview.calculation_details.balanced_scorecard', 'Leistungskarte')} {i + 1}</h5>
                            {
                              lk.calculation.result &&
                              <>
                                <div className="row">
                                  <div className="col col-md-12">
                                    <dl className="row">
                                      <dt className="col-sm-4">{editableTranslate('calculation_overview.total', 'Gesamt')}</dt>
                                      <dd className="col-sm-7">{formatCalculationAmount(lk.calculation.result.total, ' €')}</dd>
                                      {
                                        lk.calculation.result.capping.capped &&
                                        <>
                                          <dt className="col-sm-4">{editableTranslate('calculation_overview.before_capping', 'Vor Deckelung')}</dt>
                                          <dd className="col-sm-7">{formatCalculationAmount(lk.calculation.result.capping.amount.before, ' €')}</dd>
                                        </>
                                      }
                                      <dt className="col-sm-4">{editableTranslate('calculation_overview.capping', 'Deckelung')}</dt>
                                      <dd className="col-sm-7">
                                        <p><b>{translate('global.status', 'Status')}:</b> {lk.calculation.result.capping.capped ? translate('global.yes', 'Ja') : translate('global.no', 'Nein')}</p>
                                        {
                                          lk.calculation.result.capping.capped &&
                                          <p><b>{translate('calculation_overview.capping_value', 'Deckelungswert')}:</b> {formatCalculationAmount(lk.calculation.result.capping.amount.after, ' €')}</p>
                                        }
                                      </dd>
                                      <dt className="col-sm-4">{editableTranslate('calculation_overview.calculation_details.performance', 'Leistung')}</dt>
                                      <dd className="col-sm-7">
                                        <p><b>{editableTranslate('calculation_overview.calculation_details.normalized_points', 'Normierte Punkte')}:</b> {lk.calculation.result.performance.normalized_points}</p>
                                        <p><b>{editableTranslate('calculation_overview.calculation_details.pay', 'Entgelt')}:</b> {lk.calculation.result.performance.pay}</p>
                                      </dd>
                                      {
                                        lk.calculation.result.floor.enabled &&
                                        <>
                                          <dt className="col-sm-4">{editableTranslate('calculation_overview.calculation_details.floor', 'Untergrenze')}</dt>
                                          <dd className="col-sm-7">
                                            <p><b>{editableTranslate('calculation_overview.calculation_details.reduction_factor', 'Reduktionsfaktor')}:</b> {lk.calculation.result.floor.reduction_factor}</p>
                                            <p><b>{editableTranslate('calculation_overview.calculation_details.amount', 'Betrag')}:</b> {lk.calculation.result.floor.amount}</p>
                                          </dd>
                                        </>
                                      }
                                    </dl>
                                  </div>
                                </div>
                                <h5 className="card-title">{editableTranslate('calculation_overview.calculation_details.steps', 'Rechenschritte')}</h5>
                                <div className="row">
                                  <div className="col col-md-12">
                                    <dl className="row">
                                      <dd className="col-sm-11">
                                        {
                                          <CalculationResultTreeView jsonData={buildCalculationResultTreeData(lk.calculation)} employees={[selectedEmployee]} stepOnly={true} />
                                        }
                                      </dd>
                                    </dl>
                                  </div>
                                </div>
                              </>
                            }
                            {
                              !lk.calculation.result &&
                              <div className="alert alert-warning" role="alert">
                                {editableTranslate('calculation_overview.calculation_details.no_result', 'Kein Ergebnis verfügbar.')}
                              </div>
                            }
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>

    {
      showExport && <div className="col-lg-auto col-md-3 section-col-left min-height-after-navbar">
        <div className="bg-white w-100 p-4 content-card h-100">
          <div className="side-header">
            <h5 className="m-0 font-weight-bold">{editableTranslate('global.export', 'Bericht/Export')}</h5>
            <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => setShowExport(false)} data-testid="close-button">
              <i className="bi bi-x-lg"></i>
            </span>
          </div>
          <ExportForm templateKey="standard_payout_report" tableFields={exportFields} otherData={{ totalRows: pagination.totalRows, appliedFilter: (selectedBudget?.value !== '-' ? selectedBudget?.value : '') }} />
        </div>
      </div>
    }
  </>;
}
